'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Shield, Lock, User, Mail, ArrowLeft, KeyRound } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ApiError, apiClient } from '@/lib/api-client';

type View = 'login' | 'forgot' | 'reset';

type LoginValues   = { email: string; password: string };
type ForgotValues  = { email: string };
type ResetValues   = { email: string; otp: string; new_password: string; confirm_password: string };

const inputStyle = (hasError: boolean): React.CSSProperties => ({
  background:   'hsl(220,20%,10%)',
  border:       `1px solid ${hasError ? 'hsl(0,72%,51%)' : 'hsl(220,20%,18%)'}`,
  color:        'hsl(210,20%,92%)',
});

export default function LoginForm() {
  const { login } = useAuth();
  const [view, setView]               = useState<View>('login');
  const [showPassword, setShowPwd]    = useState(false);
  const [showNewPwd, setShowNewPwd]   = useState(false);
  const [isLoading, setLoading]       = useState(false);
  const [resetEmail, setResetEmail]   = useState('');

  const loginForm = useForm<LoginValues>({ defaultValues: { email: '', password: '' } });

  const onLogin = async (data: LoginValues) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
      toast.success('Welcome back!');
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.detail :
        e instanceof Error    ? e.message :
        'Login failed — please try again.';
      loginForm.setError('email', { message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const forgotForm = useForm<ForgotValues>({ defaultValues: { email: '' } });

  const onForgot = async (data: ForgotValues) => {
    setLoading(true);
    try {
      await apiClient.post('/auth/forgot-password', { email: data.email });
      setResetEmail(data.email);
      toast.success('OTP sent — check your email.');
      setView('reset');
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : 'Request failed. Try again.';
      forgotForm.setError('email', { message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = useForm<ResetValues>({
    defaultValues: { email: resetEmail, otp: '', new_password: '', confirm_password: '' },
  });

  React.useEffect(() => {
    resetForm.setValue('email', resetEmail);
  }, [resetEmail, resetForm]);

  const onReset = async (data: ResetValues) => {
    if (data.new_password !== data.confirm_password) {
      resetForm.setError('confirm_password', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', {
        email:            data.email,
        otp:              data.otp.trim(),
        new_password:     data.new_password,
        confirm_password: data.confirm_password,
      });
      toast.success('Password reset! Please log in.');
      setView('login');
      loginForm.setValue('email', data.email);
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : 'Reset failed. Check your OTP and try again.';
      resetForm.setError('otp', { message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('reason') === 'deactivated') {
    toast.error('Your account has been deactivated. Contact your administrator.');
    // Clean the URL so a refresh doesn't re-trigger the toast
    window.history.replaceState({}, '', '/login');
  }
}, []);
  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen flex" style={{ background: 'hsl(220,30%,6%)' }}>
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, hsl(220,30%,7%) 0%, hsl(220,28%,10%) 100%)' }}
      >
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(hsl(192,100%,42%) 1px, transparent 1px), linear-gradient(90deg, hsl(192,100%,42%) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }} />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ background: 'hsl(192,100%,42%)' }} />

        <div className="relative z-10 flex items-center gap-3">
          <AppLogo size={40} />
          <span className="text-2xl font-bold" style={{ color: 'hsl(192,100%,42%)' }}>Ona24</span>
        </div>

        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-bold leading-tight" style={{ color: 'hsl(210,20%,95%)' }}>
            Security Operations<br />
            <span style={{ color: 'hsl(192,100%,42%)' }}>Never Sleep.</span>
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'hsl(215,15%,55%)' }}>
            Real-time patrol monitoring, instant incident response, and complete site
            visibility — all from your SOC.
          </p>
          <div className="flex items-center gap-3 pt-2">
            {['Live Guard Tracking', 'Instant SOS Alerts', 'Shift Analytics'].map((f) => (
              <span key={f} className="text-xs font-medium px-3 py-1.5 rounded-full border" style={{
                background: 'hsl(192,100%,42%,0.08)',
                borderColor: 'hsl(192,100%,42%,0.2)',
                color: 'hsl(192,100%,60%)',
              }}>{f}</span>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-xs" style={{ color: 'hsl(215,15%,35%)' }}>
            © {new Date().getFullYear()} Ona24 · SOC Portal
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex lg:hidden items-center gap-3 mb-8">
            <AppLogo size={32} />
            <span className="text-xl font-bold" style={{ color: 'hsl(192,100%,42%)' }}>Ona24</span>
          </div>
          {children}
        </div>
      </div>
    </div>
  );

  const BackToLogin = () => (
    <button
      type="button"
      onClick={() => setView('login')}
      className="flex items-center gap-1.5 text-xs mb-6"
      style={{ color: 'hsl(215,15%,50%)' }}
    >
      <ArrowLeft size={13} /> Back to sign in
    </button>
  );

  const SubmitBtn = ({ label, loadingLabel }: { label: string; loadingLabel: string }) => (
    <button
      type="submit"
      disabled={isLoading}
      className="w-full py-3 rounded-xl text-sm font-semibold transition-all"
      style={{
        background: isLoading ? 'hsl(192,100%,30%)' : 'hsl(192,100%,42%)',
        color: 'hsl(220,30%,6%)',
        opacity: isLoading ? 0.8 : 1,
      }}
    >
      {isLoading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          {loadingLabel}
        </span>
      ) : label}
    </button>
  );

  // ════════════════════════════════════════════════════════════════════════
  // VIEW: LOGIN
  // ════════════════════════════════════════════════════════════════════════
  if (view === 'login') return (
    <Shell>
      <div className="space-y-1">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'hsl(192,100%,42%,0.12)', border: '1px solid hsl(192,100%,42%,0.2)' }}>
            <Shield size={18} style={{ color: 'hsl(192,100%,42%)' }} />
          </div>
          <span className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: 'hsl(192,100%,42%)' }}>SOC Access Portal</span>
        </div>
        <h2 className="text-2xl font-bold" style={{ color: 'hsl(210,20%,95%)' }}>Sign in</h2>
        <p className="text-sm" style={{ color: 'hsl(215,15%,50%)' }}>
          Enter your SOC credentials to access the operations centre.
        </p>
      </div>

      {/* FIXED: Enforce POST method explicitly on the HTML element */}
      <form method="POST" onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5">
        {/* Email */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'hsl(215,15%,55%)' }}>Email</label>
          <div className="relative">
            <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
              style={{ color: 'hsl(215,15%,40%)' }} />
            {/* FIXED: Added explicit name attribute */}
            <input
              type="email" 
              autoComplete="email" 
              placeholder="operator@ona24.io"
              className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none transition-all"
              style={inputStyle(!!loginForm.formState.errors.email)}
              {...loginForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
              })}
            />
          </div>
          {loginForm.formState.errors.email && (
            <p className="text-xs mt-1" style={{ color: 'hsl(0,72%,65%)' }}>
              {loginForm.formState.errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'hsl(215,15%,55%)' }}>Password</label>
            <button type="button" onClick={() => setView('forgot')}
              className="text-xs transition-colors"
              style={{ color: 'hsl(192,100%,50%)' }}>
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
              style={{ color: 'hsl(215,15%,40%)' }} />
            {/* FIXED: Added explicit name attribute */}
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password" 
              placeholder="••••••••"
              className="w-full pl-10 pr-11 py-3 rounded-xl text-sm outline-none transition-all"
              style={inputStyle(!!loginForm.formState.errors.password)}
              {...loginForm.register('password', { required: 'Password is required' })}
            />
            <button type="button" onClick={() => setShowPwd(v => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2"
              style={{ color: 'hsl(215,15%,40%)' }}>
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {loginForm.formState.errors.password && (
            <p className="text-xs mt-1" style={{ color: 'hsl(0,72%,65%)' }}>
              {loginForm.formState.errors.password.message}
            </p>
          )}
        </div>

        <SubmitBtn label="Sign in to SOC" loadingLabel="Signing in…" />
      </form>

      <p className="text-xs text-center" style={{ color: 'hsl(215,15%,35%)' }}>
        Guards use the mobile app. This portal is for SOC operators and client accounts.
      </p>
    </Shell>
  );

  // ════════════════════════════════════════════════════════════════════════
  // VIEW: FORGOT PASSWORD
  // ════════════════════════════════════════════════════════════════════════
  if (view === 'forgot') return (
    <Shell>
      <BackToLogin />

      <div className="space-y-1 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'hsl(192,100%,42%,0.12)', border: '1px solid hsl(192,100%,42%,0.2)' }}>
            <Mail size={18} style={{ color: 'hsl(192,100%,42%)' }} />
          </div>
          <span className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: 'hsl(192,100%,42%)' }}>Password Reset</span>
        </div>
        <h2 className="text-2xl font-bold" style={{ color: 'hsl(210,20%,95%)' }}>Forgot password?</h2>
        <p className="text-sm" style={{ color: 'hsl(215,15%,50%)' }}>
          Enter your email and we'll send a 6-digit OTP. It expires in 5 minutes.
        </p>
      </div>

      <form method="POST" onSubmit={forgotForm.handleSubmit(onForgot)} className="space-y-5">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'hsl(215,15%,55%)' }}>Email</label>
          <div className="relative">
            <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
              style={{ color: 'hsl(215,15%,40%)' }} />
            <input
              type="email" 
              autoComplete="email" 
              placeholder="your@email.com"
              className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none transition-all"
              style={inputStyle(!!forgotForm.formState.errors.email)}
              {...forgotForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
              })}
            />
          </div>
          {forgotForm.formState.errors.email && (
            <p className="text-xs mt-1" style={{ color: 'hsl(0,72%,65%)' }}>
              {forgotForm.formState.errors.email.message}
            </p>
          )}
        </div>

        <SubmitBtn label="Send OTP" loadingLabel="Sending…" />

        <p className="text-xs text-center" style={{ color: 'hsl(215,15%,40%)' }}>
          Already have an OTP?{' '}
          <button type="button" onClick={() => setView('reset')}
            style={{ color: 'hsl(192,100%,50%)' }}>
            Enter it here
          </button>
        </p>
      </form>
    </Shell>
  );

  // ════════════════════════════════════════════════════════════════════════
  // VIEW: RESET PASSWORD
  // ════════════════════════════════════════════════════════════════════════
  return (
    <Shell>
      <BackToLogin />

      <div className="space-y-1 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'hsl(192,100%,42%,0.12)', border: '1px solid hsl(192,100%,42%,0.2)' }}>
            <KeyRound size={18} style={{ color: 'hsl(192,100%,42%)' }} />
          </div>
          <span className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: 'hsl(192,100%,42%)' }}>Enter OTP</span>
        </div>
        <h2 className="text-2xl font-bold" style={{ color: 'hsl(210,20%,95%)' }}>Set new password</h2>
        <p className="text-sm" style={{ color: 'hsl(215,15%,50%)' }}>
          Enter the 6-digit code sent to <span style={{ color: 'hsl(192,100%,60%)' }}>{resetEmail || 'your email'}</span> and choose a new password.
        </p>
      </div>

      <form method="POST" onSubmit={resetForm.handleSubmit(onReset)} className="space-y-5">
        {/* Email */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'hsl(215,15%,55%)' }}>Email</label>
          <div className="relative">
            <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
              style={{ color: 'hsl(215,15%,40%)' }} />
            <input
              type="email"
              className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none transition-all"
              style={inputStyle(!!resetForm.formState.errors.email)}
              {...resetForm.register('email', { required: 'Email is required' })}
            />
          </div>
        </div>

        {/* OTP */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'hsl(215,15%,55%)' }}>6-digit OTP</label>
          <input
            type="text"
            inputMode="numeric" 
            maxLength={6} 
            placeholder="123456"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all text-center tracking-[0.5em] font-mono"
            style={inputStyle(!!resetForm.formState.errors.otp)}
            {...resetForm.register('otp', {
              required: 'OTP is required',
              pattern: { value: /^\d{6}$/, message: 'Must be 6 digits' },
            })}
          />
          {resetForm.formState.errors.otp && (
            <p className="text-xs mt-1" style={{ color: 'hsl(0,72%,65%)' }}>
              {resetForm.formState.errors.otp.message}
            </p>
          )}
        </div>

        {/* New password */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'hsl(215,15%,55%)' }}>New password</label>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
              style={{ color: 'hsl(215,15%,40%)' }} />
            <input
              type={showNewPwd ? 'text' : 'password'} 
              placeholder="min 8 characters"
              className="w-full pl-10 pr-11 py-3 rounded-xl text-sm outline-none transition-all"
              style={inputStyle(!!resetForm.formState.errors.new_password)}
              {...resetForm.register('new_password', {
                required: 'New password is required',
                minLength: { value: 8, message: 'Min 8 characters' },
              })}
            />
            <button type="button" onClick={() => setShowNewPwd(v => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2"
              style={{ color: 'hsl(215,15%,40%)' }}>
              {showNewPwd ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {resetForm.formState.errors.new_password && (
            <p className="text-xs mt-1" style={{ color: 'hsl(0,72%,65%)' }}>
              {resetForm.formState.errors.new_password.message}
            </p>
          )}
        </div>

        {/* Confirm password */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'hsl(215,15%,55%)' }}>Confirm password</label>
          <input
            placeholder="repeat new password"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
            style={inputStyle(!!resetForm.formState.errors.confirm_password)}
            {...resetForm.register('confirm_password', { required: 'Please confirm your password' })}
          />
          {resetForm.formState.errors.confirm_password && (
            <p className="text-xs mt-1" style={{ color: 'hsl(0,72%,65%)' }}>
              {resetForm.formState.errors.confirm_password.message}
            </p>
          )}
        </div>

        <SubmitBtn label="Reset password" loadingLabel="Resetting…" />

        <p className="text-xs text-center" style={{ color: 'hsl(215,15%,40%)' }}>
          Didn't get the code?{' '}
          <button type="button" onClick={() => setView('forgot')}
            style={{ color: 'hsl(192,100%,50%)' }}>
            Resend OTP
          </button>
        </p>
      </form>
    </Shell>
  );
}